import { useEffect, useState } from 'react';
import { CircularProgress, Box, Typography } from '@mui/material';
import { createAuthFromCookie } from '../utils/sessionAuth';
import { auth } from '../services/firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { CookieHelper } from '../utils/cookieHelper';
import { appConfig } from '../config/environment';

/**
 * Component that handles cookie-based session authentication
 * Only checks for existing session cookies and signs in if valid
 */
const CookieSessionAuthHandler: React.FC = () => {
    const [isProcessingAuth, setIsProcessingAuth] = useState(true);

    useEffect(() => {
        const processAuthFromCookie = async () => {
            try {
                // Check if session cookie exists before making API call
                const sessionCookie = CookieHelper.getCookie(appConfig.sessionCookieName);
                
                if (!sessionCookie) {
                    setIsProcessingAuth(false);
                    return;
                }

                console.debug('Session cookie found, attempting authentication...');
                
                // Create auth from cookie using the already retrieved cookie
                const customToken = await createAuthFromCookie(sessionCookie);
                if (customToken) {
                    // Sign in with the custom token
                    await signInWithCustomToken(auth, customToken);
                }
            } catch (error) {
                console.error('Auth from cookie error:', error);
            } finally {
                setIsProcessingAuth(false);
            }
        };

        processAuthFromCookie();
    }, []);

    // Show loading spinner while processing authentication
    if (isProcessingAuth) {
        return (
            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    gap: 2,
                }}
            >
                <CircularProgress />
                <Typography variant="body1" color="text.secondary">
                    Checking authentication...
                </Typography>
            </Box>
        );
    }

    // This component doesn't render anything after processing - authentication is complete
    return null;
};

export default CookieSessionAuthHandler;
